@let reportValue = report();

@if (reportValue) {
  <h2 class="text-center text-2xl font-bold mb-6">Report Connectivity & Quality</h2>

  <hr class="border-gray-300 mb-8" />

  <!-- Hardware Setup Results -->
  <h4 class="text-lg font-semibold mb-4">Hardware Setup Results</h4>

  <div class="grid md:grid-cols-3 gap-4 mb-8">
    @if (reportValue.connectivity.device) {
      <div class="flex items-center bg-white border border-gray-200 rounded shadow-sm px-4 py-3">
        <span
          class="@if (reportValue.connectivity.device.compatibility) { text-green-600 } @else { text-red-600 } mr-2">
          {{ reportValue.connectivity.device.compatibility | statusIcon }}
        </span>
        <span>Device Type: {{ reportValue.connectivity.device.type }}</span>
      </div>
    }

    @if (reportValue.connectivity.browser) {
      <div class="flex items-center bg-white border border-gray-200 rounded shadow-sm px-4 py-3">
        <span
          class="@if (reportValue.connectivity.browser.compatibility) { text-green-600 } @else { text-red-600 } mr-2">
          {{ reportValue.connectivity.browser.compatibility | statusIcon }}
        </span>
        <span>
          Browser: {{ reportValue.connectivity.browser.type }} ({{
            reportValue.connectivity.browser.version
          }})
        </span>
      </div>
    }

    @if (reportValue.connectivity.webRtc) {
      <div class="flex items-center bg-white border border-gray-200 rounded shadow-sm px-4 py-3">
        <span
          class="@if (reportValue.connectivity.webRtc.compatibility) { text-green-600 } @else { text-red-600 } mr-2">
          {{ reportValue.connectivity.webRtc.compatibility | statusIcon }}
        </span>
        <span
          >WebRTC Compatibility:
          {{ reportValue.connectivity.webRtc.compatibility ? 'Yes' : 'No' }}
        </span>
      </div>
    }
  </div>

  <!-- Connectivity Test Results -->
  <h4 class="text-lg font-semibold mb-4">Connectivity Test Results</h4>
  <div class="grid md:grid-cols-3 gap-4 mb-8">
    @let websocket = reportValue.connectivity.services.websocket;
    <div class="flex items-center bg-white border border-gray-200 rounded shadow-sm px-4 py-3">
      <span
        class="@if (websocket.status === 'success') { text-green-600 } @else { text-red-600 } mr-2">
        {{ websocket.status | statusIcon }}
      </span>
      <span>
        Websocket Status: {{ websocket.status }}
        @if (websocket.reason) {
          - {{ websocket.reason }}
        }
      </span>
    </div>

    @let link = reportValue.connectivity.services.link;
    <div class="flex items-center bg-white border border-gray-200 rounded shadow-sm px-4 py-3">
      <span class="@if (link.status === 'success') { text-green-600 } @else { text-red-600 } mr-2">
        {{ link.status | statusIcon }}
      </span>
      <span>
        Link Service Status: {{ link.status }}
        @if (link.reason) {
          - {{ link.reason }}
        }
      </span>
    </div>

    @let video = reportValue.connectivity.services.video;
    <div class="flex items-center bg-white border border-gray-200 rounded shadow-sm px-4 py-3">
      <span class="@if (video.status === 'success') { text-green-600 } @else { text-red-600 } mr-2">
        {{ video.status | statusIcon }}
      </span>
      <span>
        Video Service Status: {{ video.status }}
        @if (video.reason) {
          - {{ video.reason }}
        }
      </span>
    </div>
  </div>

  <!-- Quality Test Results -->
  <h4 class="text-lg font-semibold mb-4">Quality Test Results</h4>
  <div class="overflow-x-auto mb-8">
    <table class="min-w-full text-sm border border-gray-300 rounded border-collapse">
      <tbody>
        <tr class="bg-gray-100">
          <td class="border border-gray-300 px-4 py-2 font-bold w-1/3">Test</td>
          <td class="border border-gray-300 px-4 py-2 text-green-600">
            {{ reportValue.quality.status | statusIcon }}
          </td>
        </tr>

        <tr class="bg-gray-50">
          <th colspan="2" class="border border-gray-300 px-4 py-2 text-center">Audio</th>
        </tr>

        @let audioBitrate = reportValue.quality.audioBitrate;
        @if (audioBitrate) {
          <tr>
            <td class="border border-gray-300 px-4 py-2 font-bold">Audio quality:</td>
            <td class="border border-gray-300 px-4 py-2">
              {{ audioBitrate.mos | number: '1.0-1' }}
            </td>
          </tr>
          <tr>
            <td class="border border-gray-300 px-4 py-2 font-bold">Audio bitrate:</td>
            <td class="border border-gray-300 px-4 py-2">
              {{ $safeNavigationMigration(audioBitrate.complete?.audio?.bitrate) | toKbps }} kbps
            </td>
          </tr>
          <tr>
            <td class="border border-gray-300 px-4 py-2 font-bold">Audio packet loss:</td>
            <td class="border border-gray-300 px-4 py-2">
              {{ (audioBitrate.complete?.audio?.packetLossRatio || 0) * 100 | number: '1.0-2' }} %
            </td>
          </tr>
        }

        <tr class="bg-gray-50">
          <th colspan="2" class="border border-gray-300 px-4 py-2 text-center">Bandwidth</th>
        </tr>

        <tr>
          <td class="border border-gray-300 px-4 py-2 font-bold">Quality:</td>
          <td class="border border-gray-300 px-4 py-2">
            {{ mos() | number: '1.0-1' }}
          </td>
        </tr>

        @if (reportValue.quality.ping) {
          <tr>
            <td class="border border-gray-300 px-4 py-2 font-bold">Average Ping:</td>
            <td class="border border-gray-300 px-4 py-2">
              {{ reportValue.quality.ping.latencyMs }} ms
            </td>
          </tr>
          <tr class="bg-gray-50">
            <td class="border border-gray-300 px-4 py-2 font-bold">Min Ping:</td>
            <td class="border border-gray-300 px-4 py-2">{{ reportValue.quality.ping.min }} ms</td>
          </tr>
          <tr>
            <td class="border border-gray-300 px-4 py-2 font-bold">Max Ping:</td>
            <td class="border border-gray-300 px-4 py-2">{{ reportValue.quality.ping.max }} ms</td>
          </tr>
        }

        @if (reportValue.quality.download) {
          <tr class="bg-gray-50">
            <td class="border border-gray-300 px-4 py-2 font-bold">Download:</td>
            <td class="border border-gray-300 px-4 py-2">
              {{ reportValue.quality.download.bytesPerSecond | toMbps | number: '1.0-2' }} Mbps
              <span
                class="@if (reportValue.quality.download.status === 'success') { text-green-600 } @else { text-red-600 } ml-2">
                {{ reportValue.quality.download.status | statusIcon }}
              </span>
            </td>
          </tr>
        }

        @if (reportValue.quality.upload) {
          <tr>
            <td class="border border-gray-300 px-4 py-2 font-bold">Upload:</td>
            <td class="border border-gray-300 px-4 py-2">
              {{ reportValue.quality.upload.bytesPerSecond | toMbps | number: '1.0-2' }} Mbps
              <span
                class="@if (reportValue.quality.upload.status === 'success') { text-green-600 } @else { text-red-600 } ml-2">
                {{ reportValue.quality.upload.status | statusIcon }}
              </span>
            </td>
          </tr>
        }
      </tbody>
    </table>
  </div>

  <!-- General -->
  <h4 class="text-lg font-semibold mb-4">General</h4>
  <div class="grid grid-cols-1 gap-4 mb-8">
    <div
      class="flex items-center bg-white border border-gray-200 rounded shadow-sm w-full overflow-hidden">
      <!-- Label -->
      <span class="font-bold mr-2 px-4 py-3">Test Date:</span>
      <!-- Value -->
      <span class="px-4 py-3">
        {{
          $safeNavigationMigration(reportValue.quality.ping?.timestamp)
            | date: 'yyyy-MM-dd HH:mm:ss'
        }}
      </span>
    </div>
  </div>
}
